import {Injectable} from '@angular/core';
import {HttpService2} from '../../../services/http-service-2.0/http2.0.service';
import {asapScheduler, Observable, of, scheduled, throwError} from 'rxjs';
import {catchError, map, mergeMap, switchMap, take, toArray} from 'rxjs/operators';
import xml2js from 'xml2js';
import {MapServer, MapServerLayer, MapServerWithLayers} from './map-server.interface';
import {StorageService} from '../../../services/storage/storage.service';
import {GlobalModel} from '../../../services/state/global.model';
import {ServerType, ServerTypeVersion} from '../../../utils/map-wmslayer-generator';
import {LoggerService} from "../../../services/logger/logger.service";

@Injectable({
    providedIn: 'root'
})
export class MapServerService {

    public layersCache:any = {};

    constructor(private httpService: HttpService2, private storage:StorageService, private model:GlobalModel, protected logger:LoggerService) {
    }

    private getSingleServerStorageKey(server: MapServer): string {
        return server.serverType?.includes(ServerType.WFS)
            ? StorageService.KEY_MAP_SINGLE_WFS_ENABLED
            : StorageService.KEY_MAP_SINGLE_WMS_ENABLED;
    }

    public getMapServers(areaalId: number): Observable<MapServer[]> {
        return this.httpService.doGetRequest(`a/${areaalId}/general/map-servers/get-servers`, false).pipe(
            map(result => (<MapServer[]>result)
                .map((server) => {
                    server.active = server.defaultEnabled;

                    this.storage.getBooleanValue(`${this.getSingleServerStorageKey(server)}${server.id}`, (value) => {
                        server.active = value;
                    });
                    return server;
                })
            )
        );
    }

    public getMapServersWithLayers(areaalId: number): Observable<MapServerWithLayers[]> {
        return this.getMapServers(areaalId).pipe(
            take(1),
            switchMap((servers: MapServer[]) => {
                return scheduled(servers, asapScheduler)
            }),
            mergeMap((server: MapServer) => {
                if (!server.disabled) {
                    return this.getMapServerLayers(server).pipe(
                        catchError((err) => {
                            return of({error: {status: err.status, url: err.url, name:err.name}})
                        }),
                        map((layers: MapServerLayer[] | any) => {
                            if (layers.error?.status === 404 && layers.error?.name === 'HttpErrorResponse') {
                                return {
                                    id: server.id,
                                    name: server.name,
                                    layers: [],
                                    url: server.url,
                                    layerIds: server.layerIds,
                                    serverType: server.serverType,
                                    active: server.active,
                                    disabled: true
                                };
                            }
                            const mapServerWithLayers: MapServerWithLayers = {
                                id: server.id,
                                name: server.name,
                                layers: layers.filter(layer => {
                                    return server.layerIds.some(id => id === layer.id);
                                }),
                                url: server.url,
                                layerIds: server.layerIds,
                                serverType: server.serverType,
                                active: server.active,
                                disabled: server.disabled,
                                outputFormat: server.outputFormat,
                                expectedCRS: server.expectedCRS,
                            };
                            return mapServerWithLayers;
                        })
                    );
                } else {
                    return of({
                        id: server.id,
                        name: server.name,
                        layers: [],
                        url: server.url,
                        layerIds: server.layerIds,
                        serverType: server.serverType,
                        active: server.active,
                        disabled: server.disabled
                    });
                }
            }),
            toArray(),
        )
    }

    private getActualLayersRecursively(Layer) {
        if (Layer.Layer) {
            return this.getActualLayersRecursively(Layer.Layer)
        } else if (Layer.length > 0) {
            return Layer.reduce((_layers, layer) => {
                _layers = _layers.concat(this.getActualLayersRecursively(layer))
                return _layers;
            }, []);
        } else {
            return Layer;
        }
    }

    public getMapServerLayers(server): Observable<MapServerLayer[]> {
        if (server.serverType?.includes(ServerType.WFS)) {
            return this.getMapServerLayersWFS(server.url).pipe(
                take(1),
                map((response: any) => {
                    let parsedResponse: any = null;
                    xml2js.parseString(response, (err: any, result: any) => {
                        parsedResponse = result;
                        if (err !== null) {
                            this.logger.log('[MapServerService] Error parsing WFS data: ', err);
                        }
                    });

                    if (!parsedResponse) {
                        return [];
                    }

                    const capabilities = parsedResponse?.['wfs:WFS_Capabilities'] || parsedResponse?.WFS_Capabilities;
                    const featureTypeList = capabilities?.['wfs:FeatureTypeList']?.[0] || capabilities?.FeatureTypeList?.[0];
                    const featureTypes = featureTypeList?.['wfs:FeatureType'] || featureTypeList?.FeatureType || [];

                    const layers: MapServerLayer[] = featureTypes
                        .map((featureType: any) => {
                            const layerId = featureType?.['wfs:Name']?.[0] || featureType?.Name?.[0];
                            const layerName = featureType?.['wfs:Title']?.[0] || featureType?.Title?.[0] || layerId;

                            if (!layerId) {
                                return null;
                            }

                            return {
                                id: layerId,
                                name: layerName,
                            };
                        })
                        .filter((layer: MapServerLayer | null): layer is MapServerLayer => layer !== null);

                    this.layersCache[JSON.stringify(server)] = layers;
                    return layers;
                })
            );
        }
        if (server.serverType?.includes(ServerType.WMS)) {
            if (server.serverType.includes(ServerTypeVersion.VERSION_1_3_0)) {
                if (this.layersCache[JSON.stringify(server)]) {
                    return scheduled([this.layersCache[JSON.stringify(server)]], asapScheduler)
                }
                return this.getMapServerLayersWMS(server.url).pipe(
                    take(1),
                    map((response: any) => {
                        let parsedResponse;
                        xml2js.parseString(response, (err, result) => {
                            parsedResponse = result;
                            if (err !== null) {
                                this.logger.log('[MapServerService] Error parsing data: ', err);
                            }
                        });
                        const layers: MapServerLayer[] = [];
                        this.getActualLayersRecursively(parsedResponse.WMS_Capabilities.Capability[0].Layer[0]).forEach((layer: any) => {
                            layers.push({
                                id: layer.Name[0],
                                name: layer.Title[0],
                            });
                        });
                        this.layersCache[JSON.stringify(server)] = layers;
                        return layers;
                    })
                );
            } else {
                return throwError('version of WMS not supported yet: ' + server)
            }
        }
        // Unknown server type: return empty layers so the UI does not hang
        return scheduled([[]], asapScheduler);
    }

    private getMapServerLayersWMS(serverUrl: string, serverApiKey?: string, serverUsername?: string, ServerPassword?: string): Observable<any> {
        let param = serverUrl.includes('?') ? '&' : '?';
        return this.httpService.doExternalGetRequest(`${serverUrl}${param}request=GetCapabilities&service=WMS`, 'text');
    }

    private getMapServerLayersWFS(serverUrl: string): Observable<any> {
        let param = serverUrl.includes('?') ? '&' : '?';
        return this.httpService.doExternalGetRequest(`${serverUrl}${param}request=GetCapabilities&service=WFS&version=2.0.0`, 'text');
    }

    public checkCapabilitiesConnection(serverUrl: string, serviceType: ServerType): Observable<boolean> {
        let param = serverUrl.includes('?') ? '&' : '?';
        const requestParameters = serviceType === ServerType.WFS
            ? `${param}request=GetCapabilities&service=WFS&version=2.0.0`
            : `${param}request=GetCapabilities&service=WMS`;
        return this.httpService.doExternalGetRequest(`${serverUrl}${requestParameters}`, 'text').pipe(
            map((result: any) => {
                if (serviceType === ServerType.WFS) {
                    return !!result.includes('WFS_Capabilities') || !!result.includes('FeatureTypeList');
                }
                // TODO: Meer checks op de XML WMS connectie
                return !!result.includes('Layer');
            }),
            catchError(
                (result) => {
                    return of(false);
                }
            )
        );
    }

    public checkWMSConnection(serverUrl: string): Observable<boolean> {
        return this.checkCapabilitiesConnection(serverUrl, ServerType.WMS);
    }

    public checkWFSConnection(serverUrl: string): Observable<boolean> {
        return this.checkCapabilitiesConnection(serverUrl, ServerType.WFS);
    }
}
